import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Icon } from 'views/components/molecules/Icon';
import { InputText } from 'views/components/atoms/InputText';
import { Color, MAX_WIDTH } from 'views/constants';
import { Button } from 'views/components/atoms/Button';
import { FormattedMessage } from 'react-intl';
import { localeIds } from 'locals';
import { ErrorMessage } from 'views/components/molecules/ErrorMessage';
import { Spacer } from 'views/components/atoms/Spacer';
import { InfoMessage } from 'views/components/molecules/InfoMessage';

const Wrapper = styled.div`
  margin: 0;
  padding: 0 16px;
  background-color: ${Color.C6};
  border-bottom: 1px solid ${Color.C14};
`;

const InputWrapper = styled.div`
  max-width: ${MAX_WIDTH};
  margin: auto;
  padding: 16px 0;

  &:not(:first-of-type) {
    border-top: 1px solid ${Color.C14};
  }
`;

interface InputAreaProps {
  email: string;
  setCode(value: string): void;
}

const InputArea: React.FC<InputAreaProps> = ({ email, setCode }) => (
  <Wrapper>
    <InputWrapper>
      <InputText
        label={<Icon type="mail" />}
        defaultValue={email}
        type="email"
        disabled
        required
      />
    </InputWrapper>
    <InputWrapper>
      <FormattedMessage id={localeIds.signUpValidateForm.code}>
        {(text: string): React.ReactElement => (
          <InputText
            onChange={setCode}
            placeholder={text}
            type="text"
            required
          />
        )}
      </FormattedMessage>
    </InputWrapper>
  </Wrapper>
);

const MessageWrapper = styled.div`
  padding-top: 16px;
  display: flex;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  width: ${MAX_WIDTH};
  margin: auto;
  padding: 16px 0 28px;
`;

interface SignUpEmailValidateFormProps {
  email: string;
  errorMessage?: string;
  onResend(sentCallback: () => void): void;
  onPost(code: string): void;
}

export const SignUpEmailValidateForm: React.FC<SignUpEmailValidateFormProps> = ({
  email,
  errorMessage,
  onResend,
  onPost,
}) => {
  const [code, setCode] = useState('');
  const [
    showSentVerificationMailMessage,
    setShowSentVerificationMailMessage,
  ] = useState(true);
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setShowSentVerificationMailMessage(false);
    onPost(code);
  };

  return (
    <form onSubmit={handleSubmit}>
      <InputArea email={email} setCode={setCode} />

      {showSentVerificationMailMessage ? (
        <FormattedMessage id={localeIds.signUpValidateForm.sentMessage}>
          {(text: string): React.ReactElement => (
            <MessageWrapper>
              <InfoMessage message={text} maxWidth={MAX_WIDTH} />
            </MessageWrapper>
          )}
        </FormattedMessage>
      ) : null}
      {errorMessage ? (
        <MessageWrapper>
          <ErrorMessage message={errorMessage} maxWidth={MAX_WIDTH} />
        </MessageWrapper>
      ) : null}

      <ButtonWrapper>
        <Button
          onClick={() => {
            setShowSentVerificationMailMessage(false);
            onResend(() => {
              setShowSentVerificationMailMessage(true);
            });
          }}
          type="button"
        >
          <FormattedMessage id={localeIds.signUpValidateForm.resend} />
        </Button>

        <Spacer />

        <Button type="submit" style="primary">
          <FormattedMessage id={localeIds.signUpValidateForm.submit} />
        </Button>
      </ButtonWrapper>
    </form>
  );
};
